import { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { X, TrendingUp, CheckCircle2, Circle, Activity } from "lucide-react";

/** Doctor's view of a patient's engagement with the DATP library. */
export default function PatientEngagementModal({ userId, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/specialist/patients/${userId}/therapy-engagement`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Unable to load"));
  }, [userId]);

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" onClick={onClose} data-testid="engagement-modal">
      <div className="bg-white rounded-3xl w-full max-w-lg max-h-[92vh] overflow-y-auto p-6 lg:p-7" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <span className="uppercase-label inline-flex items-center gap-1">
              <Activity size={13} /> Therapy activity
            </span>
            <h3 className="font-display text-xl mt-1">
              {data?.patient_name ? `${data.patient_name}'s progress` : "Patient progress"}
            </h3>
          </div>
          <button onClick={onClose} className="p-1"><X size={18} /></button>
        </div>

        {error && <div className="mt-4 rounded-xl bg-rose-50 border border-rose-200 text-rose-800 p-3 text-sm">{error}</div>}

        {data && (
          <>
            <div className="mt-4 grid grid-cols-3 gap-2">
              <Stat label="Therapies" value={data.unique_therapies} />
              <Stat label="Opens" value={data.total_opens} />
              <Stat label="Completed" value={data.completions} />
            </div>

            {data.engagements.length === 0 ? (
              <div className="mt-6 rounded-xl border border-dashed border-[color:var(--mano-border)] p-6 text-center text-sm text-[color:var(--mano-ink-soft)]">
                No therapy activity yet. Consider prescribing a couple this week.
              </div>
            ) : (
              <div className="mt-5 space-y-2" data-testid="engagement-list">
                {data.engagements.map((e) => {
                  const done = !!e.completed_at;
                  return (
                    <div key={e.therapy_id} className="rounded-xl border border-[color:var(--mano-border)] p-3 flex items-center gap-3">
                      <div className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${
                        done ? "bg-emerald-100 text-emerald-700" : "bg-[color:var(--mano-primary)]/10 text-[color:var(--mano-primary)]"
                      }`}>
                        {done ? <CheckCircle2 size={14} /> : <Circle size={14} />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold truncate">{e.therapy_title}</div>
                        <div className="text-[11px] text-[color:var(--mano-ink-soft)]">
                          {e.open_count} open{e.open_count === 1 ? "" : "s"} · Last {fmtDate(e.last_opened_at)}
                          {done ? " · Completed " + fmtDate(e.completed_at) : ""}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-xl bg-[color:var(--mano-bg)] border border-[color:var(--mano-border)] p-3 text-center">
      <div className="font-display text-2xl">{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-[color:var(--mano-ink-soft)] mt-0.5">{label}</div>
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" }); }
  catch { return iso; }
}
